import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PropTypes from 'prop-types';
import {
    PrimaryButton,
    LoadingBar,
    BreadCrumbs,
    Error500,
} from '../../Common';
import {
    Grid,
    Typography,
    IconButton,
} from '@mui/material';
import { Pill } from '../../Common/Pill';
import CloseIcon from '@mui/icons-material/Close';

import Routes from '../../../routes.js';
import { DetailPanel, DetailPanelData } from '../../Common/DetailPanel.js';
import UserInfo from '../../../UserInfo.js';
import { Grey1 } from '../../../color_utils.js';
import { trakrefFontFamily } from '../../../utils.js';
import useNavigate from '../../../hooks/useNavigate.js';
import useBreadCrumbs from '../../../hooks/useBreadCrumbs.js';

/* const useStyles = makeStyles({
    leftAlign: { marginLeft: 'auto' },
    leftAlign2: { marginLeft: '1%' },
    tabs: { marginBottom: '2%' },
    indicator: { backgroundColor: '#008299' },
    headers: { marginBottom: '2%' },
}); */

const initialAliasModalValue = {
    isVisible: false,
    alias: null
};

const ApplianceDisposals = ({
    root,
    history = {},
    match: {
        params: { id: applianceDisposalID = '' },
    },
}) => {
    // ---- Page States ---- \\
    const [loading, setLoading] = useState(true);
    const [errorLoading, setErrorLoading] = useState(null);

    // --- Appliance Disposal Detail --- \\
    const [id, setId] = useState(null);
    const [name, setName] = useState('');
    const [location, setLocation] = useState('');
    const [shutdownDate, setShutdownDate] = useState('');
    const [refrigerantType, setRefrigerantType] = useState('');
    const [capacity, setCapacity] = useState('');
    const [amountReclamation, setAmountReclamation] = useState('');
    const [amountRecovered, setAmountRecovered] = useState('');
    const [notes, setNotes] = useState('');

    const navigate = useNavigate({
        pageName: 'ApplianceDisposals',
    });
    const breadCrumbs = useBreadCrumbs({
        path: { id: 'ApplianceDisposals', text: '', link: history.location.pathname }
    });

    const canEditApplianceDisposal = 
        (UserInfo.isNativeSystemOwner() &&
            (   
                UserInfo.isAdminRole() || 
                UserInfo.isTechnicianManager()
            )
        ) || UserInfo.isSystemAdministrator();

    const weightUnit = UserInfo.getWeightUnits();

    const fetchData = async () => {
        try {
            const response = await fetch(Routes.Asset.GetApplianceDisposal(applianceDisposalID));
            const data = await response.json();

            if (data.errors) {
                console.log(data.error);
                setErrorLoading(true);
                setLoading(false);
                return;
            }

            setId(data.id);
            setName(data.name);
            setLocation(data.locationName);
            setShutdownDate(data.shutdownDateDisplay);
            setRefrigerantType(data.materialTypeName);
            setCapacity(data.capacity);
            setAmountReclamation(data.amountReclamation);
            setAmountRecovered(data.amountRecovered);
            setNotes(data.notes);

            breadCrumbs.updateBreadCrumbs({ id: 'ApplianceDisposals', text: data.name, link: history.location.pathname })
            setLoading(false);
        } catch (error) {
            console.log(error);
            setErrorLoading(true);
            setLoading(false);
        }
    };

    const editAssets = () => {
        navigate.push(`/EditApplianceDisposals/${id}`);
    };

    const XButton = () => {
        // reset tab local data
        navigate.goBack('/');
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) {
        return <LoadingBar />;
    }

    if (errorLoading) {
        return <Error500 />;
    }

    return (
        <>
            <BreadCrumbs
                path={breadCrumbs.getSortedBreadCrumbs()}
            />

            <Grid container direction="row" alignItems="center" style={{ marginBottom: '1%'}} xs={10}>
                <Grid item>
                    <IconButton aria-label="close" onClick={() => XButton()}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item>
                    <Typography variant="h5" style={{ color: Grey1, fontFamily: trakrefFontFamily }}>
                        {`${name}`}
                    </Typography>
                </Grid>
                {refrigerantType && (
                    <Grid item sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Pill text={refrigerantType} type='TooltipBlueBorderOnly' bold={600} />
                    </Grid>
                )}
                {(canEditApplianceDisposal) && (
                    <Grid item sx={{ marginLeft: 'auto' }}>
                        <PrimaryButton buttonText="Edit" onClick={editAssets} />
                    </Grid>
                )} 
            </Grid>

            <Grid
                container
                justifyContent="flex-start"
                sx={{ marginBottom: '2%' }}
            >
                <Grid item xs={10}>
                    <DetailPanel disableDefaultHeight>
                        <DetailPanelData title="LOCATION" data={location} />
                        <DetailPanelData 
                            title={`APPLIANCE CAPACITY (${weightUnit})`} 
                            data={`${capacity.toFixed(3)} ${weightUnit.toLowerCase()}`} 
                        />
                        <DetailPanelData 
                            title={`AMOUNT SENT TO RECLAMATION (${weightUnit})`} 
                            data={`${amountReclamation.toFixed(3)} ${weightUnit.toLowerCase()} `} 
                        />
                        <DetailPanelData title="SHUTDOWN DATE" data={shutdownDate} isLastRow/>
                        <DetailPanelData 
                            title={`AMOUNT RECOVERED (${UserInfo.getWeightUnits()})`} 
                            data={`${amountRecovered.toFixed(3)} ${weightUnit.toLowerCase()}`}
                            isLastRow 
                        />
                        <DetailPanelData id='Notes' title="NOTES" data={notes} 
                            isLastRow
                        />
                    </DetailPanel>
                </Grid>
            </Grid>
        </>
    );
};

ApplianceDisposals.propTypes = {
    match: PropTypes.object.isRequired,
};

export default ApplianceDisposals;